/**
 * WikiDB - IndexedDB Cache for Wiki Calculator Data
 *
 * Provides fast, offline-capable access to wiki calculator data with automatic
 * background sync. Data is read from local cache first (<50ms), then synced
 * in the background.
 *
 * Usage:
 *   const wikiDB = new WikiDB();
 *   await wikiDB.init();
 *   const calcs = await wikiDB.getCalculators();
 *   const categories = await wikiDB.getCategories();
 */

const wikiBuildTime = document.lastModified;
console.log('[WikiDB] Module loaded - Last modified:', wikiBuildTime);

class WikiDB {
  constructor() {
    this.db = null;
    this.initialized = false;
    this.dbName = 'instacalc-wiki-v1';
    this.version = 1;
    this.buildTime = wikiBuildTime;
  }

  /**
   * Initialize IndexedDB database
   * Creates object stores with indexes if needed
   */
  async init() {
    if (this.initialized) return;

    try {
      console.log('[WikiDB] Initializing...');

      if (!window.indexedDB) {
        console.warn('[WikiDB] IndexedDB not supported');
        this.initialized = false;
        return;
      }

      this.db = await this._openDB();
      this.initialized = true;
      console.log('[WikiDB] Init complete');
    } catch (e) {
      console.error('[WikiDB] Initialization failed:', e);
      this.initialized = false;
      throw e;
    }
  }

  /**
   * Open or create IndexedDB database
   */
  async _openDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create 'calculators' object store with indexes
        if (!db.objectStoreNames.contains('calculators')) {
          const calcStore = db.createObjectStore('calculators', { keyPath: 'id' });
          calcStore.createIndex('category', 'category', { unique: false });
          calcStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          calcStore.createIndex('views', 'stats.views', { unique: false });
          calcStore.createIndex('tags', 'tags', { unique: false, multiEntry: true });
          console.log('[WikiDB] Created calculators store with indexes');
        }

        // Create 'metadata' object store
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
          console.log('[WikiDB] Created metadata store');
        }
      };
    });
  }

  /**
   * Get all calculators from cache
   */
  async getCalculators() {
    if (!this.initialized) await this.init();

    try {
      return await this._getAllFromStore('calculators');
    } catch (e) {
      console.error('[WikiDB] Error getting calculators:', e);
      return [];
    }
  }

  /**
   * Get calculator by ID or slug
   */
  async getCalcById(id) {
    if (!this.initialized) await this.init();

    try {
      const db = await this._ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['calculators'], 'readonly');
        const store = transaction.objectStore('calculators');
        const request = store.get(id);

        request.onsuccess = () => {
          resolve(request.result || null);
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (e) {
      console.error('[WikiDB] Error getting calc by ID:', id, e);
      return null;
    }
  }

  /**
   * Get calculator by slug (alias for category)
   */
  async getCalcBySlug(slug) {
    return this.getCalcById(slug);
  }

  /**
   * Get calculators by category
   */
  async getCalcsByCategory(category) {
    if (!this.initialized) await this.init();

    try {
      const db = await this._ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['calculators'], 'readonly');
        const store = transaction.objectStore('calculators');
        const index = store.index('category');
        const request = index.getAll(category);

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (e) {
      console.error('[WikiDB] Error getting calcs by category:', category, e);
      return [];
    }
  }

  /**
   * Get all unique categories
   */
  async getCategories() {
    if (!this.initialized) await this.init();

    try {
      // First check metadata cache
      const cachedCategories = await this._getMetadata('categories');
      if (cachedCategories && cachedCategories.data) {
        return cachedCategories.data;
      }

      // Build categories from calculators
      const calcs = await this.getCalculators();
      const categoryMap = new Map();

      calcs.forEach(calc => {
        if (calc.category) {
          if (!categoryMap.has(calc.category)) {
            categoryMap.set(calc.category, {
              id: calc.category,
              name: this._categoryToName(calc.category),
              count: 0,
              icon: this._getCategoryIcon(calc.category)
            });
          }
          const cat = categoryMap.get(calc.category);
          cat.count++;
        }
      });

      const categories = Array.from(categoryMap.values());

      // Cache categories
      await this._setMetadata('categories', { data: categories, syncedAt: Date.now() });

      return categories;
    } catch (e) {
      console.error('[WikiDB] Error getting categories:', e);
      return [];
    }
  }

  /**
   * Save calculators to cache
   */
  async saveCalculators(calculators) {
    if (!this.initialized) await this.init();

    try {
      const db = await this._ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['calculators'], 'readwrite');
        const store = transaction.objectStore('calculators');

        calculators.forEach(calc => {
          store.put(calc);
        });

        transaction.oncomplete = () => {
          console.log('[WikiDB] Saved', calculators.length, 'calculators');
          resolve(true);
        };

        transaction.onerror = () => {
          reject(transaction.error);
        };
      });
    } catch (e) {
      console.error('[WikiDB] Error saving calculators:', e);
      throw e;
    }
  }

  /**
   * Update single calculator
   */
  async updateCalculator(calc) {
    if (!this.initialized) await this.init();

    try {
      const db = await this._ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['calculators'], 'readwrite');
        const store = transaction.objectStore('calculators');
        const request = store.put(calc);

        request.onsuccess = () => {
          resolve(true);
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (e) {
      console.error('[WikiDB] Error updating calculator:', e);
      throw e;
    }
  }

  /**
   * Get sync metadata
   */
  async getLastSyncTime() {
    const sync = await this._getMetadata('lastSync');
    return sync ? sync.timestamp : 0;
  }

  /**
   * Get sync status
   */
  async getSyncStatus() {
    const status = await this._getMetadata('syncStatus');
    return status || { status: 'not-synced', progress: { current: 0, total: 0 } };
  }

  /**
   * Check if sync is needed (older than 5 minutes)
   */
  async needsSync(intervalMs = 300000) {
    const lastSync = await this.getLastSyncTime();
    return Date.now() - lastSync > intervalMs;
  }

  /**
   * Update sync metadata
   */
  async updateSyncStatus(status, progress = null) {
    return this._setMetadata('syncStatus', {
      status,
      progress,
      timestamp: Date.now()
    });
  }

  /**
   * Update last sync timestamp
   */
  async updateLastSyncTime(count = 0) {
    return this._setMetadata('lastSync', {
      timestamp: Date.now(),
      count
    });
  }

  /**
   * Clear all data (useful for reset)
   */
  async clear() {
    if (!this.initialized) await this.init();

    try {
      const db = await this._ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['calculators', 'metadata'], 'readwrite');

        transaction.objectStore('calculators').clear();
        transaction.objectStore('metadata').clear();

        transaction.oncomplete = () => {
          console.log('[WikiDB] Cache cleared');
          resolve(true);
        };

        transaction.onerror = () => {
          reject(transaction.error);
        };
      });
    } catch (e) {
      console.error('[WikiDB] Error clearing cache:', e);
      throw e;
    }
  }

  /**
   * Get count of cached calculators
   */
  async getCount() {
    if (!this.initialized) await this.init();

    try {
      const db = await this._ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['calculators'], 'readonly');
        const store = transaction.objectStore('calculators');
        const request = store.count();

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (e) {
      console.error('[WikiDB] Error getting count:', e);
      return 0;
    }
  }

  // ============ PRIVATE METHODS ============

  async _ensureDB() {
    if (!this.db) {
      this.db = await this._openDB();
    }
    return this.db;
  }

  async _getAllFromStore(storeName) {
    try {
      const db = await this._ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (e) {
      console.error('[WikiDB] Error getting all from store:', storeName, e);
      return [];
    }
  }

  async _getMetadata(key) {
    try {
      const db = await this._ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['metadata'], 'readonly');
        const store = transaction.objectStore('metadata');
        const request = store.get(key);

        request.onsuccess = () => {
          resolve(request.result || null);
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (e) {
      console.error('[WikiDB] Error getting metadata:', key, e);
      return null;
    }
  }

  async _setMetadata(key, value) {
    try {
      const db = await this._ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['metadata'], 'readwrite');
        const store = transaction.objectStore('metadata');
        const request = store.put({ key, ...value });

        request.onsuccess = () => {
          resolve(true);
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (e) {
      console.error('[WikiDB] Error setting metadata:', key, e);
      return false;
    }
  }

  /**
   * Convert category slug to display name
   */
  _categoryToName(category) {
    const names = {
      'finance': 'Finance',
      'health': 'Health',
      'science': 'Science',
      'general': 'General',
      'math': 'Math',
      'programming': 'Programming',
      'utilities': 'Utilities'
    };
    return names[category] || (category.charAt(0).toUpperCase() + category.slice(1));
  }

  /**
   * Get category icon (can be extended with emoji or SVG)
   */
  _getCategoryIcon(category) {
    const icons = {
      'finance': '💰',
      'health': '⚕️',
      'science': '🔬',
      'general': '📊',
      'math': '🔢',
      'programming': '💻',
      'utilities': '🔧'
    };
    return icons[category] || '📐';
  }
}

// Make WikiDB available globally for browser usage
window.WikiDB = WikiDB;
